import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const SHARE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://app.mngldodo.site';
const SHARE_TITLE = 'MNGL CNG Dealership Application Portal';
const SHARE_TEXT = 'Apply for CNG Dealership with Maharashtra Natural Gas Ltd. Check your land eligibility and start your application today!';

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  70% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

const Container = styled.div<{ $variant: 'horizontal' | 'floating' }>`
  display: flex;
  align-items: center;
  gap: ${p => p.$variant === 'floating' ? '8px' : '10px'};
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
  ${p => p.$variant === 'floating' ? `
    position: fixed;
    bottom: 80px;
    left: 16px;
    flex-direction: column;
    z-index: 90;
  ` : ''}
`;

const ShareLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const IconButton = styled.a<{ $bg: string; $hoverBg: string; $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: 10px;
  background: ${p => p.$bg};
  color: white;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  animation: ${popIn} 0.3s ease both;
  flex-shrink: 0;

  svg {
    width: ${p => Math.round(p.$size * 0.5)}px;
    height: ${p => Math.round(p.$size * 0.5)}px;
  }

  &:hover {
    background: ${p => p.$hoverBg};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${p => p.$bg}40;
  }
  &:active {
    transform: translateY(0);
  }
`;

const ToggleFab = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(76, 110, 245, 0.35);
  transition: all 0.2s ease;
  z-index: 91;

  svg { width: 20px; height: 20px; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(76, 110, 245, 0.45);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
`;

const IconWrapper = styled.div`
  position: relative;
  &:hover ${Tooltip} { opacity: 1; }
`;

// ─── Share Channels ──────────────────────────────────────────────────────────

const channels = [
  {
    name: 'WhatsApp',
    bg: '#25D366',
    hoverBg: '#1ebe5a',
    url: `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT + ' ' + SHARE_URL)}`,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    ),
  },
  {
    name: 'Facebook',
    bg: '#1877F2',
    hoverBg: '#0d6ce8',
    url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(SHARE_TEXT)}`,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    name: 'X (Twitter)',
    bg: '#000000',
    hoverBg: '#1a1a1a',
    url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(SHARE_URL)}`,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    name: 'Reddit',
    bg: '#FF4500',
    hoverBg: '#e03d00',
    url: `https://www.reddit.com/submit?url=${encodeURIComponent(SHARE_URL)}&title=${encodeURIComponent(SHARE_TITLE)}`,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
      </svg>
    ),
  },
  {
    name: 'Instagram',
    bg: 'linear-gradient(135deg, #833AB4, #FD1D1D, #F77737)',
    hoverBg: 'linear-gradient(135deg, #6e2f99, #e01919, #e06a2f)',
    url: `https://www.instagram.com/`,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
  },
  {
    name: 'Email',
    bg: '#64748b',
    hoverBg: '#475569',
    url: `mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodeURIComponent(SHARE_TEXT + '\n\n' + SHARE_URL)}`,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" />
      </svg>
    ),
  },
];

// ─── Component Variants ──────────────────────────────────────────────────────

interface SocialShareButtonsProps {
  variant?: 'inline' | 'floating';
  size?: number;
  showLabel?: boolean;
}

const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({
  variant = 'inline',
  size = 36,
  showLabel = true,
}) => {
  const [open, setOpen] = useState(false);

  if (variant === 'floating') {
    return (
      <>
        {open && (
          <Container $variant="floating" style={{ bottom: '132px' }}>
            {channels.map((ch, i) => (
              <IconWrapper key={ch.name}>
                <IconButton
                  href={ch.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  $bg={ch.bg}
                  $hoverBg={ch.hoverBg}
                  $size={size}
                  style={{ animationDelay: `${i * 0.04}s` }}
                  title={ch.name}
                >
                  {ch.icon}
                </IconButton>
                <Tooltip>{ch.name}</Tooltip>
              </IconWrapper>
            ))}
          </Container>
        )}
        <div style={{ position: 'fixed', bottom: '80px', left: '16px', zIndex: 91 }}>
          <ToggleFab onClick={() => setOpen(o => !o)} title="Share">
            {open ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            )}
          </ToggleFab>
        </div>
      </>
    );
  }

  // Inline variant
  return (
    <Container $variant="horizontal">
      {showLabel && <ShareLabel>Share:</ShareLabel>}
      {channels.map((ch, i) => (
        <IconWrapper key={ch.name}>
          <IconButton
            href={ch.url}
            target="_blank"
            rel="noopener noreferrer"
            $bg={ch.bg}
            $hoverBg={ch.hoverBg}
            $size={size}
            style={{ animationDelay: `${i * 0.04}s` }}
            title={ch.name}
          >
            {ch.icon}
          </IconButton>
          <Tooltip>{ch.name}</Tooltip>
        </IconWrapper>
      ))}
    </Container>
  );
};

export default SocialShareButtons;
